import React from 'react';
import { Switch, Typography } from 'antd';
import classes from './RoomSettings.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import cn from 'classnames';
import Assets from 'assets/icons';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import api from 'api';
import { setDataRoom } from 'store/slices/dataRoomSlice';
import fetchGroupsAndUsers, { fetchPermissions } from 'store/reducers/usersGroupCreator';
import {
  setIsRSidebarCollapsed,
  setIsTariffInfoModalOpen,
  setTariffInfoModalRoom,
} from 'store/slices/windowStateSlice';
import { RSidebarStack } from 'components/RightSideBar/RightSideBar';
import MessengerIntegrationModal from 'components/Modals/MessengerIntegrationModal/MessengerIntegrationModal';
import NotificationsSettingsModal from 'components/Modals/NotificationsSettingsModal/NotificationsSettingsModal';

const isBillingActive =
  window.REACT_APP_IS_BILLING_ACTIVE !== 'REPLACE_REACT_APP_IS_BILLING_ACTIVE'
    ? window.REACT_APP_IS_BILLING_ACTIVE
    : process.env.REACT_APP_IS_BILLING_ACTIVE;

const { Paragraph } = Typography;

type RoomSettingsProps = {
  stack: RSidebarStack[];
  setStack: React.Dispatch<React.SetStateAction<RSidebarStack[]>>;
}

const RoomSettings = ({}: RoomSettingsProps) => {  
  const [ellipsisDesc, setEllipsisDesc] = React.useState(true);
  const [isUpdatingWatermark, setIsUpdatingWatermark] = React.useState(false);
  const [isMessengerModalOpen, setIsMessengerModalOpen] = React.useState(false);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = React.useState(false);
  
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { selectedKeys, messageApi } = useAppSelector((store) => store.windowState);
  const { userPermissions, available_rooms } = useAppSelector((store) => store.userData);
  const { groups, rolesPermissions } = useAppSelector((store) => store.dataUsers);
    
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();  

  const isAvailablePermissionsSettings = userPermissions?.can_edit_folder_permissions;
  const isAvailableSettings = userPermissions?.can_edit_room_settings;
  const isAvailableToEditRoles = userPermissions?.can_edit_roles || userPermissions?.can_create_group_all_roles;
  const isAvailableViewLogs = userPermissions?.can_view_logs;
  const isOwnerOrFullAdmin = available_rooms.some(room => room.id === dataRoom?.id && room.is_admin);
  
  const getStatusRow = (status?: number) => {
    switch (status) {
      case 3:
        return <span className={classes.reject}>{t('Settings.terms.state.rejected')}</span>;
      case 2:
        return <span className={classes.accept}>{t('Settings.terms.state.accepted')}</span>;
      default:
        return <span className={classes.nonAccept}>{t('Settings.terms.state.nonAccepted')}</span>;
    }
  };

  const changeWatermark = async (checked: boolean) => {
    setIsUpdatingWatermark(true);

    try {
      await api.toggleRoomWatermark({ enable: !dataRoom?.watermark_enabled, id: dataRoom?.id! });
      dataRoom && dispatch(setDataRoom({...dataRoom, watermark_enabled: !dataRoom.watermark_enabled}));
      messageApi?.success(`${t('Settings.success.settingsUploaded.before')} ${dataRoom?.name}
        ${t('Settings.success.settingsUploaded.after')}`);
    } catch (e) {
      messageApi?.error(`${t('Settings.error.settingsUpload.before')} ${dataRoom?.name} 
        ${t('Settings.error.settingsUpload.after')}`);
    } finally {
      setIsUpdatingWatermark(false);
    }
  }
  
  const onConfigureRolesClick = () => {
    !rolesPermissions[0] && dispatch(fetchPermissions({ t }));
    !groups[0] && dataRoom && dispatch(fetchGroupsAndUsers({ room_id: dataRoom?.id }));
    
    dispatch(setIsRSidebarCollapsed({
      collapsed: false,
      entity: 'roles.room_setting',
      width: 300,
    }))
    // dispatch(setIsConfigureRolesOpen(true));
  };

  const onConfigurePermissionsClick = () => {
    dispatch(setIsRSidebarCollapsed({
      collapsed: false,
      entity: 'permissions.room_setting',
      width: 300,
    }))
  };

  const onMessengerIntegrationClick = () => {
    setIsMessengerModalOpen(true);
  };

  const onNotificationsSettingsClick = () => {
    setIsNotificationsModalOpen(true);
  };

  const onTariffInfoClick = () => {
    if (!dataRoom) return;
    dispatch(setTariffInfoModalRoom(dataRoom));
    dispatch(setIsTariffInfoModalOpen(true));
  };

  const showTariffInfo =
    isBillingActive === 'true' && Boolean(dataRoom?.tariff?.tariff_name);

  return (
    <>
      <div className={classes.rSidebarContent}>
        <div className={classes.description}>
          <Paragraph
            ellipsis={ellipsisDesc ? { rows: 2, expandable: true, symbol: t(`rightSideBar.more`) } : false}
            style={{fontSize: 13}}
          >
            {dataRoom?.description}
          </Paragraph>
        </div>

        {dataRoom && <div
          className={cn(classes.buttonItem, selectedKeys[0] === 'nda' ? classes.currentItem : 'null')}
          onClick={() => navigate(`/room/${dataRoom?.custom_url || dataRoom?.id}/nda`)}
          id='rsb-nda-section'
        >
          <div className={classes.buttonPrefix}>
            <Assets.LockIcon />
          </div>
          <div className={classes.buttonText}>
            {t('sidebar.nda')}
          </div>
          {dataRoom?.agreement_enable && getStatusRow(dataRoom?.user_status)}
          <div className={classes.buttonPostfix}>
            <Assets.RightIcon />
          </div>
        </div>}

        {isAvailableSettings && dataRoom && (
          <div
            className={classes.buttonItem}
            id='rsb-watermark-section'
          >
            <div className={classes.buttonPrefix}>
              <Assets.WatermarkIcon />
            </div>
            <div className={classes.buttonText}>
              {t('Settings.mark.title')}
            </div>
            <Switch
              loading={isUpdatingWatermark}
              size='small'
              onChange={changeWatermark}
              checked={dataRoom?.watermark_enabled}
            />
            <div className={classes.buttonPostfix}>
              <Assets.RightIcon />
            </div>
          </div>
        )}

        {isAvailableToEditRoles && dataRoom && (
          <div
            className={classes.buttonItem}
            onClick={onConfigureRolesClick}
            id='rsb-roles-section'
          >
            <div className={classes.buttonPrefix}>
              <Assets.RoleIcon />
            </div>
            <div className={classes.buttonText}>
              {t('Users.managment.roles')}
            </div>
            <div className={classes.buttonPostfix}>
              <Assets.RightIcon />
            </div>
          </div>
        )}

        {isAvailablePermissionsSettings && dataRoom && (
          <div
            className={classes.buttonItem}
            onClick={() => onConfigurePermissionsClick()}
            id='rsb-permissions-section'
          >
            <div className={classes.buttonPrefix}>
              <Assets.KeyIcon />
            </div>
            <div className={classes.buttonText}>
              {t('Documents.management.permissions')}
            </div>
            <div className={classes.buttonPostfix}>
              <Assets.RightIcon />
            </div>
          </div>
        )}

        {isAvailableViewLogs && dataRoom &&
          <div
            className={cn(classes.buttonItem, selectedKeys[0] === 'logs' ? classes.currentItem : 'null')}
            onClick={() => navigate(`/room/${dataRoom?.custom_url || dataRoom?.id}/logs`)}
            id='rsb-logs-section'
          >
            <div className={classes.buttonPrefix}>
              <Assets.LogIcon />
            </div>
            <div className={classes.buttonText}>
              {t('sidebar.logs')}
            </div>
            <div className={classes.buttonPostfix}>
              <Assets.RightIcon />
            </div>
          </div>
        }

        {isOwnerOrFullAdmin && dataRoom &&
          <div
            className={cn(classes.buttonItem, selectedKeys[0] === 'recycleBin' ? classes.currentItem : 'null')}
            onClick={() => navigate(`/room/${dataRoom?.custom_url || dataRoom?.id}/recycleBin`)}
            data-testid='room-menu-recycle-bin'
            id='rsb-recycle-bin-section'
          >
            <div className={classes.buttonPrefix}>
              <Assets.TrashIcon />
            </div>
            <div className={classes.buttonText}>
              {t('sidebar.recycleBin')}
            </div>
            <div className={classes.buttonPostfix}>
              <Assets.RightIcon />
            </div>
          </div>
        }

        {isOwnerOrFullAdmin && dataRoom && (
          <div
            className={classes.buttonItem}
            onClick={onMessengerIntegrationClick}
            id='rsb-messanger-section'
          >
            <div className={classes.buttonPrefix}>
              <Assets.TelegramIcon />
            </div>
            <div className={classes.buttonText}>
              {t('Settings.messengerIntegration.title')}
            </div>
            <div className={classes.buttonPostfix}>
              <Assets.RightIcon />
            </div>
          </div>
        )}
        {dataRoom && (
          <div
            className={classes.buttonItem}
            onClick={onNotificationsSettingsClick}
            id='rsb-notification-section'
          >
            <div className={classes.buttonPrefix}>
              <Assets.BellIcon />
            </div>
            <div className={classes.buttonText}>
              {t('notificationsSettings.button')}
            </div>
            <div className={classes.buttonPostfix}>
              <Assets.RightIcon />
            </div>
          </div>
        )}

        {showTariffInfo && dataRoom && (
          <div
            className={classes.buttonItem}
            onClick={onTariffInfoClick}
          >
            <div className={classes.buttonPrefix}>
              <Assets.CreditCardIcon />
            </div>
            <div className={classes.buttonText}>
              {t('TariffInfoModal.sidebarButton')}
            </div>
            <div className={classes.buttonPostfix}>
              <Assets.RightIcon />
            </div>
          </div>
        )}
      </div>
      {dataRoom && (
        <NotificationsSettingsModal
          open={isNotificationsModalOpen}
          onClose={() => setIsNotificationsModalOpen(false)}
          mode="room"
          roomId={dataRoom.id}
        />
      )}
      <MessengerIntegrationModal 
        isOpen={isMessengerModalOpen}
        onClose={() => setIsMessengerModalOpen(false)}
      />
    </>
  );
}

export default RoomSettings;
